import React from 'react';
import PropTypes from 'prop-types';

import {
    closestCenter,
    DndContext,
    KeyboardSensor,
    MouseSensor,
    TouchSensor,
    useSensor,
    useSensors
} from "@dnd-kit/core";
import {
    arrayMove,
    rectSortingStrategy,
    SortableContext,
    sortableKeyboardCoordinates
} from "@dnd-kit/sortable";

import ColAtm from "../../../../atomic/Col/Col";
import SortableMywebPhotos from "../../../../molecules/MywebPhotos/SortableMywebPhotos";
import useFlipOnReorder from "../../../../../utils/useFlipOnReorder";

// Web galeri fotoğrafları. Seçili fotoğraflar photoMapping sırasında üstte toplanır ve
// sürüklenerek sıralanabilir; seçili olmayanlar altta servisten geldiği sırada sabit durur.
// Fotoğrafın üzerindeki numara photoMapping'deki sırasıdır (bkz. MywebPhotos).
//
// WebEditAccordions bir class component olduğu için hook kullanamıyor; grid bu yüzden
// ayrı bir fonksiyon bileşeni.
const WebPhotoGrid = ({photos, photoMapping, onPhotoToggle, onOrderChange}) => {
    const sensors = useSensors(
        // 8px eşiği olmadan sürükleme, fotoğrafa tıklayıp seçme davranışını yutar
        useSensor(MouseSensor, {activationConstraint: {distance: 8}}),
        // Sürükleme tutamaçtan yapılıyor (touch-action:none), scroll çakışması yok;
        // bu yüzden çok kısa bir gecikme yeterli — sadece kazara dokunuşu ayıklar, hız hissi verir.
        useSensor(TouchSensor, {activationConstraint: {delay: 50, tolerance: 8}}),
        useSensor(KeyboardSensor, {coordinateGetter: sortableKeyboardCoordinates})
    );

    // Seçili ve seçili olmayanlar TEK listede render edilir. İki ayrı map olsaydı fotoğraf
    // grup değiştirirken unmount/mount olur, DOM düğümü yok olacağı için animasyon yapılamazdı.
    const displayPhotos = React.useMemo(() => {
        const photosById = new Map(photos.map( (photo) => [photo.photo_id, photo] ));
        const selectedPhotos = photoMapping.map( (photoId) => photosById.get(photoId) ).filter(Boolean);
        const selectedIds = new Set(selectedPhotos.map( (photo) => photo.photo_id ));

        return selectedPhotos.concat(photos.filter( (photo) => !selectedIds.has(photo.photo_id) ));
    }, [photos, photoMapping]);

    // FLIP animasyonu yalnızca SEÇİM değişince oynamalı. photoMapping sürükleme sonrası da
    // yeni referans alır, o yüzden onu doğrudan anahtar olarak kullanamayız; sıradan bağımsız,
    // yalnızca seçili kümeyi temsil eden bir anahtar üretiyoruz.
    const selectionKey = React.useMemo(
        () => photoMapping.slice().sort( (a, b) => a - b ).join(','),
        [photoMapping]
    );

    const photoGridRef = React.useRef(null);
    useFlipOnReorder(photoGridRef, selectionKey);

    const handleDragEnd = (event) => {
        const {active, over} = event;

        if( !over || active.id === over.id ){
            return;
        }

        const oldIndex = photoMapping.indexOf(active.id);
        const newIndex = photoMapping.indexOf(over.id);

        if( oldIndex === -1 || newIndex === -1 ){
            return;
        }

        onOrderChange(arrayMove(photoMapping, oldIndex, newIndex));
    };

    return (
        <ColAtm innerRef={photoGridRef}>
            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
            >
                <SortableContext
                    items={ displayPhotos.map( (photo) => photo.photo_id ) }
                    strategy={rectSortingStrategy}
                >
                    {
                        displayPhotos.map( (photo) => {
                            const isSelected = photoMapping.indexOf(photo.photo_id) !== -1;

                            return (
                                <SortableMywebPhotos
                                    key={photo.photo_id}
                                    sortableId={photo.photo_id}
                                    sortableDisabled={!isSelected}
                                    checkboxId={photo.photo_id}
                                    className="channel-box"
                                    colClassname={ isSelected ? "room-photo-grid sortable-photo" : "room-photo-grid" }
                                    blockClass="photo-select-border-box channel-border-box show-cover numbered-photos"
                                    src={photo.photo_url_thump}
                                    value={photo.photo_id}
                                    checked={isSelected}
                                    onChange={onPhotoToggle}
                                    labelClass="fact-name"
                                    htmlFor={photo.photo_id}
                                    selectedPhotos={photoMapping}
                                />
                            );
                        })
                    }
                </SortableContext>
            </DndContext>
        </ColAtm>
    );
};

WebPhotoGrid.propTypes = {
    photos: PropTypes.array.isRequired,
    // Seçili fotoğraf id'leri, kullanıcının belirlediği sırada
    photoMapping: PropTypes.array.isRequired,
    onPhotoToggle: PropTypes.func.isRequired,
    onOrderChange: PropTypes.func.isRequired,
};

export default WebPhotoGrid;
